import { useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Client-side legacy URL redirector.
 *
 * Production note: Emergent's production deployment serves the React build
 * statically via Nginx, completely bypassing `frontend/server.js`. The Node
 * server's 301 logic therefore fires in PREVIEW only. Until Emergent Support
 * adds platform-level Nginx redirect rules, we mirror those same redirects
 * here on the client so users + Googlebot land on the canonical URL with no
 * visible "Page moved" intermediary.
 *
 * SEO behavior: Returns HTTP 200 + an instant `navigate(target, replace=true)`.
 * Google reads this as a soft-301 once it executes the JS (modern Googlebot
 * runs JS). For the real 301 fix, Emergent Support must add Nginx config —
 * see `/app/docs/EMERGENT_SUPPORT_REDIRECT_MAP.md`.
 *
 * Rules mirror `frontend/server.js`:
 *   - PATH_REDIRECTS map (static aliases) → 301
 *   - /_es/* and /es/* (retired Spanish prefixes) → canonical EN (no ?lang param)
 *
 * Bare-slug → descriptive-slug product redirects are handled separately in
 * ProductDetail.jsx because they require the live product catalog to compute
 * the canonical slug.
 */

const PATH_REDIRECTS = Object.freeze({
  '/research-use-only': '/disclaimer',
  // NOTE (2026-02): the reconstitution/dose-preparation calculator was
  // removed. Legacy calculator paths intentionally NO LONGER redirect to
  // `/calculator` — they fall through to the SPA's 404 route so the
  // public site does not surface any reconstitution guidance.
  '/home': '/',
  '/index.html': '/',
  '/terms-of-use': '/terms',
  '/terms-of-service': '/terms',
  // NOTE: `/privacy-policy` is the CANONICAL legal page (returns 200) — do
  // NOT redirect it to `/privacy`. The reverse rule (`/privacy` → 200
  // `/privacy-policy`) lives in server.js.
});

const SPANISH_PREFIX_REGEX = /^\/(?:_es|es)(\/.*)?$/;

function computeTarget(pathname, search) {
  // 1) Exact path alias
  const directHit = PATH_REDIRECTS[pathname];
  if (directHit) return directHit + (search || '');

  // 2) Retired Spanish prefix → canonical EN URL (English-only site, no
  //    ?lang=es param is added — that query was retired 2026-07-18).
  const spanishMatch = pathname.match(SPANISH_PREFIX_REGEX);
  if (spanishMatch) {
    const rest = spanishMatch[1] || '';
    // Strip the /_es or /es prefix; preserve the rest of the path. Default
    // to '/' if the prefix was the whole URL. Also DROP any pre-existing
    // `lang=es|en` query — the site is English-only.
    const clean = rest === '' ? '/' : rest;
    const params = new URLSearchParams(search || '');
    params.delete('lang');
    const qs = params.toString();
    return `${clean}${qs ? `?${qs}` : ''}`;
  }

  return null;
}

export default function LegacyRedirector() {
  const location = useLocation();
  const navigate = useNavigate();

  // useLayoutEffect runs synchronously BEFORE the page paints — so the user
  // sees the destination, not the legacy URL's placeholder content.
  useLayoutEffect(() => {
    const target = computeTarget(location.pathname, location.search);
    if (target && target !== location.pathname + location.search) {
      navigate(target, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
}

// Exported for the unit test that locks the redirect map shape.
export { PATH_REDIRECTS, computeTarget };
